import React, { useState, useRef, useEffect, useCallback } from 'react';
import { webvonkClient } from '../lib/supabase';
import { saveKoffieSession } from '../koffiereparatie-geleen/KoffieApp';
import { initKmfcClient } from '../lib/supabase';
import { Eye, EyeOff, LayoutDashboard, LogOut, X, Bold, Italic, Underline, Strikethrough, Highlighter } from 'lucide-react';

const HIGHLIGHT_COLORS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fecaca', '#e9d5ff', '#fed7aa'];

function RichEditor({ value, onChange, initialHTML }) {
  const editorRef = useRef(null);
  const [showColors, setShowColors] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !initialized.current) {
      editorRef.current.innerHTML = initialHTML || '';
      initialized.current = true;
    }
  }, [initialHTML]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || '');
  };

  const handleInput = () => onChange(editorRef.current?.innerHTML || '');

  const handleKeyDown = (e) => {
    if (e.key !== ' ') return;
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    const text = node.textContent?.substring(0, range.startOffset) || '';
    if (text === '-') {
      e.preventDefault();
      // Verwijder de '-' en zet een bullet list in
      document.execCommand('delete', false);
      document.execCommand('insertUnorderedList', false);
      onChange(editorRef.current?.innerHTML || '');
    }
  };

  const toolBtn = (onClick, children, title) => (
    <button type="button" title={title} onMouseDown={e => { e.preventDefault(); onClick(); }} style={{
      background: 'transparent', border: 'none', borderRadius: 6, padding: '5px 7px',
      cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#333',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.07)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >{children}</button>
  );

  return (
    <div style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 10, overflow: 'visible', background: 'rgba(0,0,0,0.04)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 2, padding: '6px 8px',
        borderBottom: '1px solid rgba(0,0,0,0.08)', flexWrap: 'wrap', position: 'relative',
      }}>
        {toolBtn(() => exec('bold'),          <Bold size={14} />,          'Vet')}
        {toolBtn(() => exec('italic'),        <Italic size={14} />,        'Cursief')}
        {toolBtn(() => exec('underline'),     <Underline size={14} />,     'Onderstrepen')}
        {toolBtn(() => exec('strikeThrough'), <Strikethrough size={14} />, 'Doorstrepen')}
        <div style={{ width: 1, height: 16, background: 'rgba(0,0,0,0.12)', margin: '0 2px' }} />
        <div style={{ position: 'relative' }}>
          {toolBtn(() => setShowColors(v => !v), <Highlighter size={14} />, 'Markeerkleur')}
          {showColors && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
              background: '#fff', border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 10, padding: 6, display: 'flex', gap: 4,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            }}>
              {HIGHLIGHT_COLORS.map(c => (
                <button key={c} type="button" onMouseDown={e => { e.preventDefault(); exec('hiliteColor', c); setShowColors(false); }} style={{
                  width: 20, height: 20, borderRadius: 5, background: c,
                  border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', padding: 0,
                }} />
              ))}
              <button type="button" onMouseDown={e => { e.preventDefault(); exec('hiliteColor', 'transparent'); setShowColors(false); }} style={{
                width: 20, height: 20, borderRadius: 5, border: '1px solid rgba(0,0,0,0.15)',
                cursor: 'pointer', padding: 0, fontSize: 11, background: '#fff', color: '#999',
              }}>✕</button>
            </div>
          )}
        </div>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        style={{
          minHeight: 100, padding: '10px 14px', fontSize: 14, color: '#111',
          outline: 'none', lineHeight: 1.6, fontFamily: 'system-ui, sans-serif',
          wordBreak: 'break-word',
        }}
      />
    </div>
  );
}

const COMPANIES = [
  { name: 'KMFC Shop', slug: 'kmfc-shop' },
  { name: 'Koffiereparatie Geleen', slug: 'koffiereparatie-geleen' },
  { name: 'Webvonk (intern)', slug: null },
];

const TAGS = [
  { label: 'Update',  color: '#6366f1' },
  { label: 'Fix',     color: '#f59e0b' },
  { label: 'Nieuw',   color: '#22c55e' },
  { label: 'Let op',  color: '#ef4444' },
];

const COMPANY_OPTIONS = [
  'Algemeen',
  'Algemeen - Mijn Webvonk web',
  'Algemeen - Mijn Webvonk iOS',
  'Algemeen - Mijn Webvonk Android',
  'KMFC Shop',
  'Koffiereparatie Geleen',
];

function getSession() {
  try {
    const sa = JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null');
    if (sa?.username) return { ...sa, _type: 'superadmin', company_name: 'Webvonk' };
  } catch {}
  try {
    const kmfc = JSON.parse(localStorage.getItem('kmfc_user') || 'null');
    if (kmfc?.username) return { ...kmfc, _type: 'kmfc' };
  } catch {}
  try {
    const koffie = JSON.parse(localStorage.getItem('koffie_session') || 'null');
    if (koffie?.username) return { ...koffie, _type: 'koffie' };
  } catch {}
  return null;
}

function getInitials(session) {
  if (!session) return null;
  if (session._type === 'superadmin') return 'WV';
  const company = session.company_name || session.company_slug || '';
  const words = company.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (session.username || 'WV').slice(0, 2).toUpperCase();
}

function matchesSequential(name, query) {
  const n = name.toLowerCase(), q = query.toLowerCase();
  if (!n.startsWith(q[0])) return false;
  let i = 0;
  for (const char of n) { if (char === q[i]) i++; if (i === q.length) return true; }
  return false;
}

function getDashboardUrl(session) {
  if (!session) return null;
  if (session.company_slug === 'koffiereparatie-geleen') return '/#/Koffiereparatie/dashboard';
  if (session.company_slug === 'kmfc-shop') return '/#/KMFC/orders';
  if (localStorage.getItem('webvonk_superadmin')) return '/#/company-select';
  return null;
}

function logout() {
  localStorage.removeItem('kmfc_user');
  localStorage.removeItem('koffie_session');
  localStorage.removeItem('webvonk_superadmin');
  window.location.reload();
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
    + ' om ' + new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

const inputStyle = {
  width: '100%', padding: '10px 14px',
  background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: 10, fontSize: 14, color: '#111', outline: 'none',
  fontFamily: 'system-ui, sans-serif', boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'rgba(0,0,0,0.5)', marginBottom: 6, fontFamily: 'system-ui, sans-serif',
};

export default function LogsPage() {
  const session = getSession();
  const isSuperAdmin = session?._type === 'superadmin';
  const initials = getInitials(session);

  const [posts, setPosts]           = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [openPost, setOpenPost]     = useState(null);
  const [showLogin, setShowLogin]   = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const overlayRef = useRef(null);

  // login form
  const [companyQuery, setCompanyQuery]           = useState('');
  const [selectedCompany, setSelectedCompany]     = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError]     = useState('');

  // compose form
  const [cTitle,   setCTitle]   = useState('');
  const [cBody,    setCBody]    = useState('');
  const [cTag,     setCTag]     = useState('Update');
  const [cVersion, setCVersion] = useState('');
  const [cCompany, setCCompany] = useState('Algemeen');
  const [cSaving,  setCSaving]  = useState(false);
  const [cError,   setCError]   = useState('');

  const filteredCompanies = companyQuery.length > 0
    ? COMPANIES.filter(c => matchesSequential(c.name, companyQuery))
    : [];

  // Load posts
  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    const { data } = await webvonkClient
      .from('logs_posts')
      .select('*')
      .order('created_at', { ascending: false });
    setPosts(data || []);
    setPostsLoading(false);
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
    };
    if (showProfileMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showProfileMenu]);

  useEffect(() => {
    document.body.style.overflowY = 'scroll';
    return () => { document.body.style.overflowY = ''; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!selectedCompany) { setLoginError('Selecteer eerst je bedrijf.'); return; }
    if (!username || !password) { setLoginError('Vul alle velden in.'); return; }
    setLoginLoading(true); setLoginError('');
    try {
      const email = `${username.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')}@webvonk.app`;
      const { data: authData, error: authError } = await webvonkClient.auth.signInWithPassword({ email, password });
      if (authError || !authData?.user) throw new Error('Foute inloggegevens.');
      const { data: profile, error: profileError } = await webvonkClient
        .from('Users').select('User_id, Username, Company_slug, Role_id, Companies(Company_name)')
        .eq('User_id', authData.user.id).single();
      if (profileError || !profile) throw new Error('Foute inloggegevens.');
      const roleMap = {
        1: { slug: 'admin', can_view_new_orders: true, can_view_order_history: true, can_manage_settings: true, can_edit_product_data: true, can_change_product_status: true, can_view_product_statistics: true, can_view_revenue_statistics: true },
        2: { slug: 'manager', can_view_new_orders: true, can_view_order_history: true, can_manage_settings: true, can_edit_product_data: true, can_change_product_status: true, can_view_product_statistics: true, can_view_revenue_statistics: true },
        3: { slug: 'medewerker', can_view_new_orders: true, can_view_order_history: false, can_manage_settings: false, can_edit_product_data: false, can_change_product_status: true, can_view_product_statistics: false, can_view_revenue_statistics: false },
      };
      if (profile.Company_slug === null && profile.Role_id === 1 && selectedCompany.slug === null) {
        localStorage.setItem('webvonk_superadmin', JSON.stringify({
          id: authData.user.id, username: profile.Username, role_id: 1,
          access_token: authData.session.access_token, refresh_token: authData.session.refresh_token, expires_at: authData.session.expires_at,
        }));
        window.location.reload(); return;
      }
      if (profile.Company_slug !== selectedCompany.slug) throw new Error('Geen toegang tot dit bedrijf.');
      const userData = {
        id: authData.user.id, username: profile.Username,
        company_slug: profile.Company_slug, company_name: profile.Companies?.Company_name ?? '',
        role_id: profile.Role_id, role: roleMap[profile.Role_id] ?? roleMap[3],
        access_token: authData.session.access_token, refresh_token: authData.session.refresh_token,
        expires_at: authData.session.expires_at, login_time: new Date().toISOString(),
      };
      if (profile.Company_slug === 'koffiereparatie-geleen') { saveKoffieSession(userData); }
      else { await initKmfcClient(); localStorage.setItem('kmfc_user', JSON.stringify(userData)); }
      window.location.reload();
    } catch (err) {
      setLoginError(err.message || 'Er is iets misgegaan.');
    } finally { setLoginLoading(false); }
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setCTitle(post.title);
    setCBody(post.body);
    setCTag(post.tag);
    setCVersion(post.version || '');
    setCCompany(post.company);
    setOpenPost(null);
    setShowCompose(true);
  };

  const handleCompose = async (e) => {
    e.preventDefault();
    if (!cTitle.trim() || !cBody.trim()) { setCError('Vul titel en bericht in.'); return; }
    setCSaving(true); setCError('');
    const tagObj = TAGS.find(t => t.label === cTag) || TAGS[0];
    const payload = {
      title: cTitle.trim(), body: cBody.trim(),
      tag: tagObj.label, tag_color: tagObj.color,
      company: cCompany,
      version: tagObj.label === 'Update' && cVersion.trim() ? cVersion.trim() : null,
    };
    let error;
    if (editingPost) {
      ({ error } = await webvonkClient.from('logs_posts')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('id', editingPost.id));
    } else {
      ({ error } = await webvonkClient.from('logs_posts')
        .insert({ ...payload, author: session.username }));
    }
    if (error) { setCError('Opslaan mislukt: ' + error.message); setCSaving(false); return; }
    setCTitle(''); setCBody(''); setCTag('Update'); setCVersion(''); setCCompany('Algemeen');
    setEditingPost(null);
    setShowCompose(false);
    await loadPosts();
    setCSaving(false);
  };

  const userCompany = session?.company_name || session?.company_slug || null;
  const visiblePosts = session
    ? posts.filter(p => isSuperAdmin || p.company === userCompany || p.company === 'Algemeen' || p.company.startsWith('Algemeen -'))
    : [];

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa', position: 'relative', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Top right */}
      <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 100 }} ref={profileRef}>
        {session ? (
          <div style={{ position: 'relative' }}>
            <div onClick={() => setShowProfileMenu(v => !v)} style={{
              width: 40, height: 40, borderRadius: '50%', background: '#111',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.04em',
              userSelect: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', cursor: 'pointer',
            }}>
              {initials}
            </div>
            {showProfileMenu && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(48px) saturate(200%)',
                WebkitBackdropFilter: 'blur(48px) saturate(200%)', border: '1px solid rgba(255,255,255,0.5)',
                borderRadius: 16, padding: 6, minWidth: 200,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)',
              }}>
                <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)', marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{session.username}</div>
                  <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.4)', marginTop: 1 }}>{session.company_name || 'Webvonk'}</div>
                </div>
                {getDashboardUrl(session) && (
                  <button onClick={() => { window.location.href = getDashboardUrl(session); }} style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
                    background: 'transparent', border: 'none', borderRadius: 10, cursor: 'pointer',
                    fontSize: 13, fontWeight: 500, color: '#111', textAlign: 'left',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <LayoutDashboard size={15} style={{ opacity: 0.6 }} /> Dashboard openen
                  </button>
                )}
                <button onClick={logout} style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px',
                  background: 'transparent', border: 'none', borderRadius: 10, cursor: 'pointer',
                  fontSize: 13, fontWeight: 500, color: '#dc2626', textAlign: 'left',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,38,38,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <LogOut size={15} /> Uitloggen
                </button>
              </div>
            )}
          </div>
        ) : (
          <button onClick={() => setShowLogin(true)} style={{
            padding: '8px 18px', background: '#111', color: '#fff',
            border: 'none', borderRadius: 999, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}>
            Inloggen
          </button>
        )}
      </div>

      {/* Page */}
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '80px 24px 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          {session && (
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {session.company_name || 'Webvonk'}
              </span>
            </div>
          )}
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#0a0a0a', margin: '0 0 6px', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            Updates
          </h1>
          <p style={{ fontSize: 15, color: '#888', margin: 0, lineHeight: 1.6 }}>Mijn Webvonk</p>
        </div>

        {/* Compose — alleen superadmin */}
        {isSuperAdmin && (
          <div onClick={() => setShowCompose(true)} style={{
            background: '#fff', border: '1.5px dashed #e5e5e5', borderRadius: 16,
            padding: '16px 20px', marginBottom: 32,
            display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'border-color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#ccc'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e5e5'}
          >
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: '#111',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
            }}>{initials}</div>
            <span style={{ fontSize: 14, color: '#bbb' }}>Nieuwe update schrijven...</span>
          </div>
        )}

        {/* Posts */}
        {!session
          ? <p style={{ fontSize: 14, color: '#bbb' }}>Log in om updates te bekijken.</p>
          : postsLoading
          ? <p style={{ fontSize: 14, color: '#bbb' }}>Laden...</p>
          : !visiblePosts.length
          ? <p style={{ fontSize: 14, color: '#bbb' }}>Geen updates gevonden.</p>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {visiblePosts.map((post, i) => (
                <div key={post.id}>
                  <div onClick={() => setOpenPost(post.id)} style={{
                    background: '#fff', borderRadius: 16, padding: '18px 20px',
                    border: '1px solid #f0f0f0', cursor: 'pointer', userSelect: 'none',
                  }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: '#555',
                        background: '#f0f0f0', padding: '3px 9px',
                        borderRadius: 999, flexShrink: 0, whiteSpace: 'nowrap',
                      }}>{post.company.startsWith('Algemeen - ') ? post.company.replace('Algemeen - ', '') : post.company}</span>
                      <span style={{
                        fontSize: 15, fontWeight: 700, color: '#0a0a0a',
                        letterSpacing: '-0.01em', minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{post.title}</span>
                      {post.version && (
                        <span style={{ fontSize: 13, color: '#aaa', fontStyle: 'italic', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
                          &nbsp;v{post.version}
                        </span>
                      )}
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 16, color: '#ccc', flexShrink: 0, lineHeight: 1 }}>›</span>
                    </div>
                  </div>
                  {i < visiblePosts.length - 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0' }}>
                      <div style={{ width: 1, height: 14, background: '#eee' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        }
      </div>

      {/* Post detail popup — liquid glass */}
      {openPost && (() => {
        const post = visiblePosts.find(p => p.id === openPost);
        if (!post) return null;
        return (
          <div onClick={() => setOpenPost(null)} style={{
            position: 'fixed', inset: 0,
            background: 'rgba(100,100,100,0.18)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            zIndex: 1000, padding: '40px 20px',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'rgba(255,255,255,0.55)',
              backdropFilter: 'blur(48px) saturate(220%)',
              WebkitBackdropFilter: 'blur(48px) saturate(220%)',
              border: '1px solid rgba(255,255,255,0.7)',
              borderRadius: 28,
              padding: '32px 28px',
              width: '100%', maxWidth: 480,
              maxHeight: '88vh', overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              boxShadow: '0 8px 64px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(255,255,255,0.3)',
              position: 'relative',
            }}>
              {/* Close + edit */}
              <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 6 }}>
                {isSuperAdmin && (
                  <button onClick={() => openEdit(post)} style={{
                    background: 'rgba(0,0,0,0.07)', border: 'none', borderRadius: 8,
                    padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#555',
                  }}>Bewerken</button>
                )}
                <button onClick={() => setOpenPost(null)} style={{
                  background: 'rgba(0,0,0,0.07)', border: 'none', borderRadius: '50%',
                  width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#555',
                }}><X size={15} /></button>
              </div>

              {/* Tag */}
              <span style={{
                fontSize: 11, fontWeight: 700, color: post.tag_color,
                background: post.tag_color + '18', padding: '4px 11px',
                borderRadius: 999, letterSpacing: '0.05em', display: 'inline-block', marginBottom: 14,
              }}>{post.tag}</span>

              {/* Title */}
              <h2 style={{
                fontSize: 20, fontWeight: 800, color: '#0a0a0a',
                letterSpacing: '-0.03em', lineHeight: 1.2, margin: '0 0 12px',
              }}>{post.title}</h2>

              {/* Meta */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#555', background: 'rgba(0,0,0,0.07)', padding: '3px 9px', borderRadius: 999 }}>
                  {post.company}
                </span>
                {post.version && (
                  <>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)' }}>·</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', background: '#6366f114', padding: '3px 9px', borderRadius: 999 }}>
                      v{post.version}
                    </span>
                  </>
                )}
                <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)' }}>·</span>
                <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>Gepubliceerd: {formatDate(post.created_at)}</span>
                <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)' }}>·</span>
                <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{post.author}</span>
                {post.updated_at && (
                  <>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)' }}>·</span>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>Bewerkt: {formatDate(post.updated_at)}</span>
                  </>
                )}
              </div>

              {/* Body */}
              {post.body && post.body.includes('<') ? (
                <div style={{ fontSize: 15, color: '#444', lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: post.body }} />
              ) : (
                <div style={{ fontSize: 15, color: '#444', lineHeight: 1.7 }}>
                  {post.body.split('\n').map((line, i) => {
                    const bullet = line.match(/^-\s+(.+)/);
                    if (bullet) return (
                      <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%', background: post.tag_color,
                          flexShrink: 0, display: 'inline-block', marginTop: 2,
                        }} />
                        <span>{bullet[1]}</span>
                      </div>
                    );
                    return line.trim() ? <p key={i} style={{ margin: '0 0 6px' }}>{line}</p> : null;
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Compose modal */}
      {showCompose && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowCompose(false); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 24, padding: '32px 28px',
            width: '100%', maxWidth: 480, boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111', letterSpacing: '-0.02em' }}>
                {editingPost ? 'Update bewerken' : 'Nieuwe update'}
              </h2>
              <button onClick={() => { setShowCompose(false); setEditingPost(null); }} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 4, display: 'flex',
              }}><X size={18} /></button>
            </div>
            <form onSubmit={handleCompose}>
              {/* Tag + Bedrijf naast elkaar */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Type</label>
                  <select value={cTag} onChange={e => setCTag(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {TAGS.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Bedrijf</label>
                  <select value={cCompany} onChange={e => setCCompany(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {COMPANY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              {cTag === 'Update' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Versienummer</label>
                  <input style={inputStyle} type="text" value={cVersion} onChange={e => setCVersion(e.target.value)} placeholder="bijv. 2.1.3" />
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Titel</label>
                <input style={inputStyle} type="text" value={cTitle} onChange={e => setCTitle(e.target.value)} placeholder="Wat is er veranderd?" />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Bericht</label>
                <RichEditor key={editingPost?.id || 'new'} value={cBody} onChange={setCBody} initialHTML={cBody} />
              </div>
              {cError && <p style={{ margin: '0 0 16px', fontSize: 13, color: '#dc2626' }}>{cError}</p>}
              <button type="submit" disabled={cSaving} style={{
                width: '100%', padding: '12px', background: '#111', color: '#fff',
                border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
                cursor: cSaving ? 'not-allowed' : 'pointer', opacity: cSaving ? 0.6 : 1,
              }}>
                {cSaving ? 'Opslaan...' : editingPost ? 'Opslaan' : 'Publiceren'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Login modal */}
      {showLogin && (
        <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) setShowLogin(false); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(180,180,180,0.15)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(48px) saturate(200%)',
            WebkitBackdropFilter: 'blur(48px) saturate(200%)', border: '1px solid rgba(255,255,255,0.22)',
            borderRadius: 28, padding: '36px 32px', width: '100%', maxWidth: 380,
            boxShadow: '0 4px 60px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)',
          }}>
            <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 700, color: '#111', letterSpacing: '-0.02em' }}>
              Inloggen
            </h2>
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16, position: 'relative' }}>
                <label style={labelStyle}>Bedrijf</label>
                <input style={inputStyle} type="text" value={companyQuery}
                  onChange={e => { setSelectedCompany(null); setCompanyQuery(e.target.value); setShowCompanyDropdown(true); }}
                  onFocus={() => { if (companyQuery.length > 0) setShowCompanyDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 150)}
                  placeholder="Zoek bedrijf..." autoComplete="off" disabled={loginLoading}
                />
                {showCompanyDropdown && filteredCompanies.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(40px)',
                    border: '1px solid rgba(255,255,255,0.5)', borderRadius: 12, marginTop: 4, overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                  }}>
                    {filteredCompanies.map(c => (
                      <div key={c.slug ?? 'null'} onMouseDown={() => { setSelectedCompany(c); setCompanyQuery(c.name); setShowCompanyDropdown(false); }}
                        style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#111', fontWeight: 500 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >{c.name}</div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Gebruikersnaam</label>
                <input style={inputStyle} type="text" value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" disabled={loginLoading} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Wachtwoord</label>
                <div style={{ position: 'relative' }}>
                  <input style={{ ...inputStyle, paddingRight: 42 }} type={showPassword ? 'text' : 'password'}
                    value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" disabled={loginLoading} />
                  <button type="button" onClick={() => setShowPassword(v => !v)} style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(0,0,0,0.4)', padding: 0, display: 'flex',
                  }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {loginError && <p style={{ margin: '0 0 16px', fontSize: 13, color: '#dc2626' }}>{loginError}</p>}
              <button type="submit" disabled={loginLoading} style={{
                width: '100%', padding: '11px', background: 'rgba(255,255,255,0.9)', color: '#111',
                border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
                cursor: loginLoading ? 'not-allowed' : 'pointer', opacity: loginLoading ? 0.6 : 1,
              }}>
                {loginLoading ? 'Bezig...' : 'Inloggen'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
